import React from 'react';
import { Typography, Icon, Tooltip, Button } from '@scale/scaleui';
import {
  SelectWorkerSkill,
  SelectWorkerSkillCategory,
} from 'react-scale/corp/components/UI/SelectWorkerSkill';
import { IExtendedSignupFlow, WorkerSkillDimensionQualityCategory } from '@scale/shared-types';
import _ from 'lodash';

export const SignupFlowIntendedWorkerSkills = ({
  signupFlowConfig,
  setSignupFlowConfig,
  shouldSetAutoGranted = false,
}: {
  signupFlowConfig: Partial<IExtendedSignupFlow>;
  setSignupFlowConfig: (signupFlowConfig: Partial<IExtendedSignupFlow>) => void;
  shouldSetAutoGranted?: boolean;
}) => {
  const onChangeSkillSelection = (workerSkillId: string | undefined, index: number) => {
    const newIntendedWorkerSkills = (signupFlowConfig.intendedWorkerSkills ?? [])?.map(
      (intendedWorkerSkill, i) =>
        i === index && workerSkillId
          ? {
              ...intendedWorkerSkill,
              workerSkillId,
            }
          : intendedWorkerSkill,
    );
    setSignupFlowConfig({
      ...signupFlowConfig,
      intendedWorkerSkills: newIntendedWorkerSkills,

      // Reset autoGrantIntendedWorkerSkills if no skills are present
      autoGrantIntendedWorkerSkills: shouldSetAutoGranted && newIntendedWorkerSkills.length > 0,
    });
  };

  const onRemoveIntendedWorkerSkill = (index: number) => {
    const newIntendedWorkerSkills = (signupFlowConfig.intendedWorkerSkills ?? [])?.filter(
      (_, i) => i !== index,
    );
    setSignupFlowConfig({
      ...signupFlowConfig,
      intendedWorkerSkills: newIntendedWorkerSkills,

      // Reset autoGrantIntendedWorkerSkills if no skills are present
      autoGrantIntendedWorkerSkills: shouldSetAutoGranted && newIntendedWorkerSkills.length > 0,
    });
  };

  // If shouldSetAutoGranted is true (rendering in conditionalConfig section) and the flow has Intended Worker Skills set up,
  // don't allow adding skills.
  const shouldAllowAddingSkills =
    !shouldSetAutoGranted ||
    !!signupFlowConfig.autoGrantIntendedWorkerSkills ||
    !signupFlowConfig.intendedWorkerSkills?.length;
  const skillsToShow = shouldAllowAddingSkills ? signupFlowConfig.intendedWorkerSkills ?? [] : [];

  return (
    <>
      {!shouldSetAutoGranted && (
        <div className="col-span-1 flex items-center self-start pr-1">
          <Typography.Paragraph3>Intended Worker Skills</Typography.Paragraph3>
          <Tooltip
            text={'Specific worker skills that the user is intended to have'}
            target={
              <div>
                <Icon icon={['fas', 'circle-info']} />
              </div>
            }
            side="bottom"
          />
        </div>
      )}

      <div className="flex flex-col col-span-3 gap-2">
        {skillsToShow.map((intendedWorkerSkill, index) => (
          <div key={index} className="flex items-center gap-2 w-full">
            <div key={index} className="flex items-center gap-2 w-full">
              <SelectWorkerSkill
                className="w-48"
                value={intendedWorkerSkill.workerSkillId}
                onChange={value => onChangeSkillSelection(value, index)}
              />
              <SelectWorkerSkillCategory
                className="w-36"
                value={intendedWorkerSkill.category}
                onChange={(value: WorkerSkillDimensionQualityCategory | undefined) => {
                  setSignupFlowConfig({
                    ...signupFlowConfig,
                    intendedWorkerSkills: signupFlowConfig.intendedWorkerSkills?.map(
                      (intendedWorkerSkill, i) =>
                        i === index
                          ? {
                              ...intendedWorkerSkill,
                              category: value ?? WorkerSkillDimensionQualityCategory.SelfReported,
                            }
                          : intendedWorkerSkill,
                    ),
                  });
                }}
              />
              <Icon
                className="cursor-pointer"
                icon={['fas', 'times']}
                size="sm"
                variant="neutral"
                onClick={() => onRemoveIntendedWorkerSkill(index)}
              />
            </div>
          </div>
        ))}

        <div className={!_.isEmpty(signupFlowConfig.intendedWorkerSkills) ? 'mt-2 ml-0' : ''}>
          <Button
            variant="neutral"
            size="sm"
            onClick={() => {
              setSignupFlowConfig({
                ...signupFlowConfig,
                intendedWorkerSkills: [
                  ...(signupFlowConfig.intendedWorkerSkills ?? []),
                  {
                    workerSkillId: '',
                    category: WorkerSkillDimensionQualityCategory.SelfReported,
                  },
                ],
                autoGrantIntendedWorkerSkills: shouldSetAutoGranted,
              });
            }}
            disabled={
              signupFlowConfig.intendedWorkerSkills?.some(
                intendedWorkerSkill => !intendedWorkerSkill.workerSkillId,
              ) || !shouldAllowAddingSkills
            }
          >
            Add Worker Skill
          </Button>
        </div>
      </div>
    </>
  );
};
